// ── Voice chat — API client ───────────────────────────────────────────────────
// Base: process.env.REACT_APP_MyAMP_Url + /api/web/voice
// Auth: Bearer token from localStorage('authToken')
//
// The web voice page's own endpoints on MyAMP (routes/web there), deliberately
// separate from the mobile app's /api/mobile/* even though they behave the same,
// so that a change made for one client can't break the other.
//
// Two response shapes:
//   - non-streaming routes answer { isSuccessful, errorMessage, data } — unwrapped
//     here, a refusal becomes a VoiceApiError carrying the server's message;
//   - chat streams server-sent events, one `data: {json}` frame per line, six
//     kinds (VoiceFrame). A failure after the stream opens arrives as an `error`
//     frame, and a refusal before it opens is turned into one here, so a caller
//     reading a turn only ever handles frames.
//
// A 401 on any call made with the session token signs the user out through the
// global fetch interceptor (utils/authInterceptors). A stream can't carry a 401
// once it has started, so an `error` frame with isUnauthorized does the same here.

import { handleUnauthorized } from '../../utils/apiClient';

const BASE = `${process.env.REACT_APP_MyAMP_Url ?? ''}/api/web/voice`;

function token(): string {
  return localStorage.getItem('authToken') ?? '';
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return { Authorization: `Bearer ${token()}`, ...extra };
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Transcript {
  text: string;
  /** ISO 639-3 as the speech service detected it ("eng", "ukr"). */
  language: string;
  durationSeconds: number;
  /** Something voice-shaped reached the microphone but no words were heard in it. Not an error. */
  isSilent: boolean;
}

export interface VoiceOption {
  voiceId: string;
  name: string;
  category: string;
  description: string;
  labels: Record<string, string>;
  hasPreview: boolean;
}

export interface ConfirmationDetail {
  label: string;
  value: string;
}

export interface Confirmation {
  summary: string;
  action: string;
  details: ConfirmationDetail[];
}

export interface FrameArtifact {
  id: string;
  fileName: string;
}

/** One tool the agent called during a turn, with what it sent and got back. */
export interface FrameToolStep {
  toolName: string;
  toolPayload: string;
  toolResponse: string;
}

/**
 * One frame of a turn. A field the server had nothing to say about is absent,
 * not empty — read it with a default.
 */
export type VoiceFrame =
  | { kind: 'delta'; text: string }
  | { kind: 'status'; status: string; estimatedSeconds?: number }
  | { kind: 'confirmation'; conversationId: string; confirmation: Confirmation }
  | { kind: 'artifact'; artifacts: FrameArtifact[] }
  | {
      kind: 'done';
      conversationId: string;
      artifacts: FrameArtifact[];
      canGenerateArtifact: boolean;
      /** What the agent did this turn; absent on a reattached turn. */
      toolHistory?: FrameToolStep[];
    }
  | { kind: 'error'; errorMessage: string; conversationId?: string; isUnauthorized?: boolean };

export interface ActiveTurn {
  active: boolean;
  question?: string;
  chatId?: string;
  /** ISO with an offset. */
  startedAt?: string;
}

export interface TurnRequest {
  /** What the user said — the transcript, verbatim. */
  prompt: string;
  /** The conversation to continue, or a fresh id the page minted for a new one. */
  conversationId: string;
  /** Which assistant; omitted means the account's primary agent. */
  agentId?: string;
  /** Name for a new conversation; the server uses the opening question when omitted. */
  title?: string;
}

export class VoiceApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'VoiceApiError';
  }
}

// ── Envelope ──────────────────────────────────────────────────────────────────

interface Envelope<T> {
  isSuccessful: boolean;
  errorMessage: string | null;
  data: T;
}

async function readEnvelope<T>(res: Response): Promise<T> {
  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    // Not json — a proxy's error page, or a FastAPI 401 ({detail}) that parsed but
    // isn't an envelope. Falls through to the status below.
  }
  if (res.ok && body?.isSuccessful) return body.data;
  throw new VoiceApiError(body?.errorMessage || `Request failed (HTTP ${res.status})`, res.status);
}

/** Audio bytes, or null when the server answered 204 (nothing to play). */
async function readAudio(res: Response): Promise<Blob | null> {
  if (res.status === 204) return null;
  if (!res.ok) await readEnvelope<never>(res);
  const blob = await res.blob();
  return blob.size > 0 ? blob : null;
}

// ── Recognition ───────────────────────────────────────────────────────────────

function transcriptionQuery(sampleRate?: number): string {
  const params = new URLSearchParams();
  if (sampleRate) params.set('sampleRate', String(sampleRate));
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * One utterance as raw samples: 16-bit signed little-endian mono at `sampleRate`.
 * The page's ordinary path — the server builds the wav container.
 */
export async function transcribePcm(
  pcm: ArrayBuffer | Int16Array<ArrayBuffer>,
  sampleRate: number,
  signal?: AbortSignal,
): Promise<Transcript> {
  const res = await fetch(`${BASE}/transcription/stream${transcriptionQuery(sampleRate)}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/octet-stream' }),
    body: pcm,
    signal,
  });
  return readEnvelope<Transcript>(res);
}

// ── Streaming an utterance while it's spoken (Chrome and Edge) ───────────────
// The utterance goes up as it's spoken, so when the user stops only the last
// moment is still in flight — the same thing the mobile app does. It needs a
// request body that can be written to while it's being sent, which only
// Chromium has, and only over HTTP/2 (Chrome refuses it over HTTP/1.1, as a
// local dev server is). Anywhere it can't work, the caller sends the utterance
// whole instead, and `disableUtteranceStreaming` stops it being tried again.

/** An upload being written as the user speaks. */
export interface UtteranceUpload {
  /** More samples, as they're captured. */
  write(samples: Int16Array): void;
  /** No more samples: the transcript. Rejects with a TypeError when the connection can't stream. */
  finish(): Promise<Transcript>;
  /** Drops the upload — the utterance turned out to be noise, or the turn was cancelled. */
  abort(): void;
}

let streamingDisabled = false;

/** Whether this browser can send a request body as it's written (Chromium's `duplex: 'half'`). */
export const canStreamRequests: boolean = (() => {
  try {
    if (typeof ReadableStream === 'undefined' || typeof Request === 'undefined') return false;
    let duplexAccessed = false;
    const hasContentType = new Request('https://example.invalid', {
      method: 'POST',
      body: new ReadableStream(),
      get duplex() {
        duplexAccessed = true;
        return 'half';
      },
    } as RequestInit).headers.has('Content-Type');
    return duplexAccessed && !hasContentType;
  } catch {
    return false;
  }
})();

let streamingSupportOverride: boolean | null = null;

/** For tests: pretend the runtime can (or can't) stream request bodies. */
export function overrideStreamingSupportForTests(supported: boolean | null): void {
  streamingSupportOverride = supported;
}

/** Stops streaming for the rest of the page's life — after a connection refused it. */
export function disableUtteranceStreaming(): void {
  streamingDisabled = true;
}

/**
 * Opens an upload to /transcription/stream for samples at `sampleRate`, or
 * null where streaming can't be used — the caller then sends the utterance
 * whole once it's finished.
 */
export function openUtteranceUpload(sampleRate: number, signal?: AbortSignal): UtteranceUpload | null {
  if (!(streamingSupportOverride ?? canStreamRequests) || streamingDisabled) return null;

  let controller: ReadableStreamDefaultController<Uint8Array> | null = null;
  const body = new ReadableStream<Uint8Array>({
    start(c) {
      controller = c;
    },
  });
  const abort = new AbortController();
  signal?.addEventListener('abort', () => abort.abort());

  const result = fetch(`${BASE}/transcription/stream${transcriptionQuery(sampleRate)}`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/octet-stream' }),
    body,
    duplex: 'half',
    signal: abort.signal,
  } as RequestInit).then(readEnvelope<Transcript>);
  // Observed here too, so a refusal before finish() isn't an unhandled rejection.
  result.catch(() => {});

  let closed = false;
  return {
    write(samples) {
      if (closed || !controller) return;
      // Copied: the capture reuses nothing, but a view into a shared buffer would send the whole buffer.
      controller.enqueue(new Uint8Array(samples.buffer.slice(samples.byteOffset, samples.byteOffset + samples.byteLength)));
    },
    finish() {
      if (!closed) {
        closed = true;
        controller?.close();
      }
      return result;
    },
    abort() {
      closed = true;
      abort.abort();
    },
  };
}

/** A recorded file (e.g. MediaRecorder's webm). The name's extension says the format. */
export async function transcribeFile(
  audio: Blob,
  fileName: string,
  signal?: AbortSignal,
): Promise<Transcript> {
  const form = new FormData();
  form.append('file', audio, fileName);
  const res = await fetch(`${BASE}/transcription`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
    signal,
  });
  return readEnvelope<Transcript>(res);
}

// ── Playback ──────────────────────────────────────────────────────────────────

export interface SpeechRequest {
  text: string;
  voiceId: string;
  /** 0.7–1.2; the server clamps. Omit for the voice's own pace. */
  speed?: number;
  /** Neighbouring pieces of a split answer, so each is read as part of the whole. */
  previousText?: string;
  nextText?: string;
}

/** The text read aloud as mp3, or null when there's nothing to play (no voice, or an empty answer). */
export async function synthesize(request: SpeechRequest, signal?: AbortSignal): Promise<Blob | null> {
  const res = await fetch(`${BASE}/speech`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(request),
    signal,
  });
  return readAudio(res);
}

/** The voices an answer can be read in, sorted by name. `refresh` skips the server's hour-long cache. */
export async function listVoices(refresh = false): Promise<VoiceOption[]> {
  const res = await fetch(`${BASE}/speech/voices${refresh ? '?refresh=true' : ''}`, {
    headers: authHeaders(),
  });
  const data = await readEnvelope<{ voices: VoiceOption[] }>(res);
  return data.voices ?? [];
}

/**
 * A sample of one voice as mp3, or null when it has none. A VoiceApiError with
 * status 404 means the voice is gone — the list is stale and worth refreshing.
 */
export async function fetchVoicePreview(voiceId: string, signal?: AbortSignal): Promise<Blob | null> {
  const res = await fetch(`${BASE}/speech/voices/${encodeURIComponent(voiceId)}/preview`, {
    headers: authHeaders(),
    signal,
  });
  return readAudio(res);
}

// ── Settings ──────────────────────────────────────────────────────────────────

/** The user's voice chat settings — kept on the account, shared with the mobile app. */
export interface VoiceSettings {
  /** '' means the first stock voice. */
  voiceId: string;
  speed: number;
  voiceChoiceOffered: boolean;
  /** True when the account has never saved any (these are the defaults). */
  isDefault?: boolean;
}

export async function getVoiceSettings(): Promise<VoiceSettings> {
  const res = await fetch(`${BASE}/settings`, { headers: authHeaders() });
  return readEnvelope<VoiceSettings>(res);
}

/** Changes some settings; answers all of them as they now stand. */
export async function updateVoiceSettings(changes: Partial<Omit<VoiceSettings, 'isDefault'>>): Promise<VoiceSettings> {
  const res = await fetch(`${BASE}/settings`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(changes),
  });
  return readEnvelope<VoiceSettings>(res);
}

// ── Chat ──────────────────────────────────────────────────────────────────────

/** Whether a turn is still being generated for this conversation — asked before reattaching. */
export async function getActiveTurn(conversationId: string): Promise<ActiveTurn> {
  const res = await fetch(`${BASE}/chat/active/${encodeURIComponent(conversationId)}`, {
    headers: authHeaders(),
  });
  return readEnvelope<ActiveTurn>(res);
}

/** Send one spoken turn and read the answer frame by frame. Abort `signal` to stop reading. */
export function streamTurn(request: TurnRequest, signal?: AbortSignal): AsyncGenerator<VoiceFrame> {
  const form = new FormData();
  form.append('human_prompt', request.prompt);
  form.append('conversationId', request.conversationId);
  form.append('agent_id', request.agentId ?? '');
  form.append('nc_token', localStorage.getItem('amsToken') ?? '');
  if (request.title) form.append('title', request.title);

  return readFrames(
    fetch(`${BASE}/chat`, {
      method: 'POST',
      headers: authHeaders({ Accept: 'text/event-stream' }),
      body: form,
      signal,
    }),
    request.conversationId,
  );
}

/**
 * Pick up a turn already in flight — the whole answer so far is replayed. Reading
 * it costs nothing upstream: the turn keeps running whether or not anyone watches.
 */
export function reattachTurn(conversationId: string, signal?: AbortSignal): AsyncGenerator<VoiceFrame> {
  return readFrames(
    fetch(`${BASE}/chat/reattach/${encodeURIComponent(conversationId)}`, {
      headers: authHeaders({ Accept: 'text/event-stream' }),
      signal,
    }),
    conversationId,
  );
}

async function* readFrames(request: Promise<Response>, conversationId: string): AsyncGenerator<VoiceFrame> {
  const res = await request;

  // Refused before the stream opened (empty prompt, foreign agent, unknown
  // conversation…): said as an error frame so the caller has one path for failures.
  if (!res.ok || !res.body) {
    try {
      await readEnvelope<never>(res);
    } catch (err) {
      yield {
        kind: 'error',
        errorMessage: err instanceof Error ? err.message : 'The assistant could not answer this. Please try again.',
        conversationId,
      };
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffered += decoder.decode(value, { stream: true });

      // A frame's json never contains a raw newline (the server escapes them), so
      // every complete line is a whole frame — or a ": keepalive" comment, or blank.
      let newline = buffered.indexOf('\n');
      while (newline >= 0) {
        const frame = parseLine(buffered.slice(0, newline));
        buffered = buffered.slice(newline + 1);
        if (frame) {
          if (frame.kind === 'error' && frame.isUnauthorized) handleUnauthorized();
          yield frame;
        }
        newline = buffered.indexOf('\n');
      }
    }
    const last = parseLine(buffered + decoder.decode());
    if (last) yield last;
  } finally {
    // Stops the download when the caller stops reading early (an interruption).
    reader.cancel().catch(() => {});
  }
}

function parseLine(line: string): VoiceFrame | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('data:')) return null;
  try {
    const frame = JSON.parse(trimmed.slice(5).trim()) as VoiceFrame;
    return frame && typeof frame.kind === 'string' ? frame : null;
  } catch {
    // A frame that doesn't parse is skipped rather than ending the turn.
    return null;
  }
}
